import React from "react";
import Dropdown from "../../common/dropDown";
import IsfReferenceDatePicker from "./IsfReferenceDatePicker";
import IsfReferenceDateHint from "./IsfReferenceDateHint";
import { formatSiteDisplayLabel } from "./isfSiteSetupService";
import "./isfSiteSetupWizard.scss";

function RequiredLabel({ children }) {
  return (
    <label className="form-label">
      {children} <span className="text-danger">*</span>
    </label>
  );
}

export default function IsfSiteTemplateFields({
  siteRows = [],
  templateOptions = [],
  sameTemplateForAll = true,
  onSameTemplateForAllChange,
  sharedTemplate = null,
  onSharedTemplateChange,
  perSiteTemplates = {},
  onPerSiteTemplateChange,
  sharedReferenceDate = "",
  onSharedReferenceDateChange,
  perSiteReferenceDates = {},
  onPerSiteReferenceDateChange,
  disabled = false,
  showSharedTemplate = true,
  embedded = false,
  notice = "",
}) {
  const rows = (siteRows || []).filter((row) => row.SiteId);
  if (rows.length <= 1) return null;

  return (
    <div
      className={`isf-site-template-fields ${
        embedded ? "isf-site-template-fields--embedded" : ""
      }`}
    >
      <div className="isf-site-template-fields__intro">
        <span className="isf-site-template-fields__count">{rows.length}</span>
        <div>
          <p className="isf-site-template-fields__title">Multi-site setup</p>
          <p className="isf-site-template-fields__hint">
            Choose one template for every site, or assign a different template per site.
          </p>
        </div>
      </div>

      <div className="isf-site-template-fields__mode" role="radiogroup" aria-label="Template mode">
        <button
          type="button"
          className={`isf-site-template-fields__mode-btn ${
            sameTemplateForAll ? "is-active" : ""
          }`}
          onClick={() => onSameTemplateForAllChange?.(true)}
          disabled={disabled}
        >
          <span className="isf-site-template-fields__mode-icon" aria-hidden>
            <i className="fa fa-copy" />
          </span>
          <span className="isf-site-template-fields__mode-text">
            <strong>Same template</strong>
            <small>Apply one template to all sites</small>
          </span>
        </button>
        <button
          type="button"
          className={`isf-site-template-fields__mode-btn ${
            !sameTemplateForAll ? "is-active" : ""
          }`}
          onClick={() => onSameTemplateForAllChange?.(false)}
          disabled={disabled}
        >
          <span className="isf-site-template-fields__mode-icon" aria-hidden>
            <i className="fa fa-sitemap" />
          </span>
          <span className="isf-site-template-fields__mode-text">
            <strong>Per site</strong>
            <small>Pick a template for each site</small>
          </span>
        </button>
      </div>

      {notice ? (
        <p className="isf-site-template-fields__notice" role="status">
          {notice}
        </p>
      ) : null}

      {sameTemplateForAll && showSharedTemplate ? (
        <div className="row g-2 isf-site-template-fields__pair-row">
          <div className="col-md-6">
            <div className="isf-site-template-fields__field">
              <RequiredLabel>Template for all sites</RequiredLabel>
              <Dropdown
                options={templateOptions}
                selected={sharedTemplate}
                onSelect={onSharedTemplateChange}
                placeholder="Select template"
                isMulti={false}
                disabled={disabled}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="isf-site-template-fields__field">
              <RequiredLabel>Reference date (all sites)</RequiredLabel>
              <IsfReferenceDatePicker
                value={sharedReferenceDate}
                onChange={onSharedReferenceDateChange}
                disabled={disabled}
              />
              <IsfReferenceDateHint />
            </div>
          </div>
        </div>
      ) : null}

      {!sameTemplateForAll ? (
        <div className="isf-site-template-fields__site-list">
          {rows.map((site, index) => (
            <div key={site.SiteId} className="isf-site-template-fields__site-card">
              <div className="isf-site-template-fields__site-head">
                <span className="isf-site-template-fields__site-index">{index + 1}</span>
                <div className="isf-site-template-fields__site-info">
                  <strong>
                    {site.SiteName?.trim() || formatSiteDisplayLabel(site)}
                  </strong>
                  {site.SiteNumber?.trim() ? (
                    <span className="isf-site-template-fields__site-sub">
                      {site.SiteNumber.trim()}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="row g-2 isf-site-template-fields__pair-row">
                <div className="col-md-6">
                  <div className="isf-site-template-fields__field isf-site-template-fields__field--compact">
                    <RequiredLabel>Template</RequiredLabel>
                    <Dropdown
                      options={templateOptions}
                      selected={perSiteTemplates[site.SiteId] || null}
                      onSelect={(opt) => onPerSiteTemplateChange?.(site.SiteId, opt || null)}
                      placeholder="Select template"
                      isMulti={false}
                      disabled={disabled}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="isf-site-template-fields__field isf-site-template-fields__field--compact">
                    <RequiredLabel>Reference date</RequiredLabel>
                    <IsfReferenceDatePicker
                      value={perSiteReferenceDates[site.SiteId] || ""}
                      onChange={(value) =>
                        onPerSiteReferenceDateChange?.(site.SiteId, value)
                      }
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
